// Helpers for weekly-recurring Remember items.
//
// A recurring item carries:
//   recurrence: { weekdays: number[] (0=Sun..6=Sat), endDate: "YYYY-MM-DD" }
//   completedDates: string[]   // individual occurrence dates marked done
//   completed: boolean         // whole series ended (no more occurrences)
//
// All date math is done with local "YYYY-MM-DD" strings to avoid timezone
// off-by-one issues.

import { formatDate } from "./dateUtils";

// Weekday metadata in Mon..Sun display order, mapped to JS getDay() values.
export const WEEKDAYS = [
  { value: 1, short: "M", label: "Monday" },
  { value: 2, short: "T", label: "Tuesday" },
  { value: 3, short: "W", label: "Wednesday" },
  { value: 4, short: "Th", label: "Thursday" },
  { value: 5, short: "F", label: "Friday" },
  { value: 6, short: "S", label: "Saturday" },
  { value: 0, short: "Su", label: "Sunday" },
];

export const isRecurring = (item) =>
  !!(item && item.recurrence && Array.isArray(item.recurrence.weekdays));

// Parse a "YYYY-MM-DD" string into a local Date at midnight.
const parseLocal = (str) => {
  if (!str) return null;
  const [y, m, d] = String(str).split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

export const todayStr = () => formatDate(new Date());

// Short weekday letters for the selected weekdays, in Mon..Sun order.
export const weekdayShortList = (weekdays = []) => {
  const set = new Set(weekdays);
  return WEEKDAYS.filter((w) => set.has(w.value)).map((w) => w.short);
};

// The earliest occurrence date (>= fromStr) that is on a selected weekday,
// is on/before endDate, and is not already in completedDates. Null if none.
export const getNextOccurrence = (item, fromStr = todayStr()) => {
  if (!isRecurring(item) || item.completed) return null;
  const weekdays = item.recurrence.weekdays || [];
  if (weekdays.length === 0) return null;
  const end = parseLocal(item.recurrence.endDate);
  if (!end) return null;
  const completed = new Set(item.completedDates || []);

  let cursor = parseLocal(fromStr);
  if (!cursor) return null;
  // Bound the loop strictly by the end date.
  while (cursor <= end) {
    const dateStr = formatDate(cursor);
    if (weekdays.includes(cursor.getDay()) && !completed.has(dateStr)) {
      return dateStr;
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return null;
};

// Whether a recurring item still has any upcoming (today-or-later) occurrence.
export const hasUpcomingOccurrence = (item, fromStr = todayStr()) =>
  getNextOccurrence(item, fromStr) !== null;

// Every occurrence date in [startStr, throughStr] on a selected weekday and
// on/before the recurrence end date.
const occurrencesBetween = (item, startStr, throughStr) => {
  const out = [];
  if (!isRecurring(item)) return out;
  const weekdays = item.recurrence.weekdays || [];
  const end = parseLocal(item.recurrence.endDate);
  let cursor = parseLocal(startStr);
  const through = parseLocal(throughStr);
  if (!cursor || !through || !end) return out;
  const limit = through < end ? through : end;
  while (cursor <= limit) {
    if (weekdays.includes(cursor.getDay())) out.push(formatDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
};

// Build a history view for a recurring item: occurrences from its creation
// date up to today (each Completed/Missed/Today), plus a count of upcoming
// occurrences (after today, up to the end date).
export const getOccurrenceHistory = (item, nowStr = todayStr()) => {
  if (!isRecurring(item)) {
    return { entries: [], completed: 0, missed: 0, upcoming: 0 };
  }
  const completedSet = new Set(item.completedDates || []);
  const startStr = item.createdAt
    ? formatDate(new Date(item.createdAt))
    : nowStr;

  const pastAndToday = occurrencesBetween(item, startStr, nowStr);
  const entries = pastAndToday.map((date) => {
    let status;
    if (completedSet.has(date)) status = "completed";
    else if (date < nowStr) status = "missed";
    else status = "today";
    return { date, status };
  });

  const completed = entries.filter((e) => e.status === "completed").length;
  const missed = entries.filter((e) => e.status === "missed").length;

  // Upcoming = future occurrences (strictly after today) not yet completed.
  const end = parseLocal(item.recurrence.endDate);
  let upcoming = 0;
  if (end) {
    const cursor = parseLocal(nowStr);
    if (cursor) {
      cursor.setDate(cursor.getDate() + 1); // start from tomorrow
      while (cursor <= end) {
        const dateStr = formatDate(cursor);
        if (
          item.recurrence.weekdays.includes(cursor.getDay()) &&
          !completedSet.has(dateStr)
        ) {
          upcoming += 1;
        }
        cursor.setDate(cursor.getDate() + 1);
      }
    }
  }

  return { entries, completed, missed, upcoming };
};
